import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { MemberService } from '@app/core/members/member.service';
import { V2SignupConfirmationAbstract } from '@app/+login/deprecated-hybrid/components/v2-signup-confirmation.abstract.component';
import { SettingsService } from '@app/core/settings/settings.service';
import { SessionService } from '@app/core/session/session.service';
import { LoginService } from '@app/+login/login.service';
import { JumptoErrorDirective } from '@app/shared/directives/jumpto-error.directive';
import { FormControl, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { InputErrorsComponent } from '@app/shared/components/input-errors/input-errors.component';
import { LetDirective } from '@ngrx/component';
import { TranslatePipe } from '@ngx-translate/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { SignupConsentsComponent } from '@app/+login/deprecated-hybrid/components/signup-consents/signup-consents.component';
import { PrivacyPolicyComponent } from '@app/shared/components/privacy-policy/privacy-policy.component';
import { GetConsentsTranslationPipe } from '@app/shared/pipes/get-consents-translation.pipe';
import { AppFocusableDirective } from '@app/shared/directives/focusable.directive';
import { MissingIdentifierInfoComponent } from '@app/+login/deprecated-hybrid/components/missing-identifier-info/missing-identifier-info.component';

@Component({
  templateUrl: './magic-link-signup-confirmation.component.html',
  imports: [
    InputErrorsComponent,
    LetDirective,
    TranslatePipe,
    NgClass,
    JumptoErrorDirective,
    ReactiveFormsModule,
    SignupConsentsComponent,
    PrivacyPolicyComponent,
    GetConsentsTranslationPipe,
    AsyncPipe,
    AppFocusableDirective,
    MissingIdentifierInfoComponent,
  ],
})
export class MagicLinkSignupConfirmationComponent extends V2SignupConfirmationAbstract implements OnDestroy, OnInit {
  @ViewChild('appJumptoError') public appJumptoError?: JumptoErrorDirective;

  public readonly consentsData = this.route.snapshot.data.consentsData;

  public readonly otp$ = this.route.queryParamMap.pipe(
    map(params => params.get('otp')),
  );

  constructor(cdr: ChangeDetectorRef,
              router: Router, route: ActivatedRoute,
              settingsService: SettingsService,
              sessionService: SessionService,
              loginService: LoginService,
              private memberService: MemberService) {
    super(cdr, router, route, sessionService, loginService, settingsService);

    if (this.consentsData.isConsentAvailable && this.consentsData.containsIdentifiers) {
      this.form.addControl('consents', new UntypedFormControl({}));
    }
    if (this.consentsData.isConsentAvailable) {
      this.form.addControl('tos', new FormControl<boolean>(false, Validators.requiredTrue));
    }
  }

  public ngOnInit(): void {
    super.ngOnInit();

    this.prepareFormOnFormValuesChanges();
  }

  public onSubmit(): void {
    this.otp$.pipe(
      switchMap(otp => this.signUp(otp).pipe(
        switchMap(() => this.signIn(otp))),
      ),
      takeUntil(this.unsubscribe$),
    ).subscribe({
      next: () => this.onSignInSuccess(),
      error: () => this.onSignInError(),
    });
  }

  private signUp(password: string | null): Observable<any> {
    if (!this.identifier) {
      this.onError();
      return EMPTY;
    }

    this.hasSignInError = false;

    const payload = {
      [this.identifier.identifier_type]: this.identifier.identifier,
      registration_password: password,
      ...this.form.value.consents.sms_marketing && this.identifier.identifier_type === this.identifierEmailType ? { msisdn: this.form.value.msisdn } : {},
      ...this.form.value.consents.email_marketing && this.identifier.identifier_type === this.identifierMsisdnType ? { email: this.form.value.email } : {},
      consents: this.form.value.consents,
    };
    return this.memberService.createV3(payload).pipe(
      tap(() => this.onSuccess()),
      catchError(error => {
        this.onError(error);
        setTimeout(() => this.appJumptoError?.check());
        return throwError(error);
      }),
    );
  }
}
